if (!customElements.get('bundle-upsell')) {
  customElements.define(
    'bundle-upsell',
    class BundleUpsell extends HTMLElement {
      constructor() {
        super();

        this.onFormSubmit = this.onFormSubmit.bind(this);
        this.onBundleChange = this.onBundleChange.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
        this.onModalClick = this.onModalClick.bind(this);
      }

      connectedCallback() {
        this.bundleData = this.readBundleData();
        if (!this.bundleData?.complementary?.length) return;

        this.productInfo = this.closest('product-info');
        this.productForm = this.productInfo?.querySelector('product-form');
        this.form = this.productForm?.querySelector('form[data-type="add-to-cart-form"]');
        this.submitButton = this.productForm?.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton?.querySelector('span');
        this.checkbox = this.querySelector('[data-bundle-checkbox]');
        this.heading = this.querySelector('[data-bundle-heading]');
        this.description = this.querySelector('[data-bundle-description]');
        this.addonVariantContainer = this.querySelector('[data-addon-variant-container]');
        this.openModalButton = this.querySelector('[data-open-bundle-modal]');
        this.modal = this.openModalButton ? document.getElementById(this.openModalButton.dataset.openBundleModal) : null;
        this.caretMarkup = this.querySelector('.select .svg-wrapper')?.outerHTML || '';

        this.mainVariant = this.getCurrentMainVariant();
        this.addonProduct = this.getInitialAddonProduct();
        this.addonVariant = this.getInitialAddonVariant(this.addonProduct);

        this.form?.addEventListener('submit', this.onFormSubmit, true);
        this.addEventListener('change', this.onBundleChange);
        this.openModalButton?.addEventListener('click', (event) => {
          event.preventDefault();
          this.modal?.show(this.openModalButton);
        });
        this.modal?.addEventListener('click', this.onModalClick);
        this.productInfo?.classList.add('bundle-upsell-mounted');

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
        }

        this.updateView();
      }

      disconnectedCallback() {
        this.form?.removeEventListener('submit', this.onFormSubmit, true);
        this.removeEventListener('change', this.onBundleChange);
        this.modal?.removeEventListener('click', this.onModalClick);
        this.productInfo?.classList.remove('bundle-upsell-mounted', 'bundle-upsell-active');
        this.variantChangeUnsubscriber?.();
      }

      readBundleData() {
        const script = this.querySelector('[data-bundle-products-json]');
        if (!script) return null;

        try {
          return JSON.parse(script.textContent);
        } catch (error) {
          console.error('Bundle upsell JSON could not be parsed', error);
          return null;
        }
      }

      onBundleChange(event) {
        if (event.target.matches('[data-bundle-checkbox]')) {
          this.updateView();
          return;
        }

        if (event.target.matches('[data-addon-variant-select]')) {
          this.addonVariant = this.findVariant(this.addonProduct, event.target.value);
          this.updateAddonPrice();
          this.updateSummary();
          this.updateSubmitButton();
        }
      }

      onVariantChange({ data }) {
        if (data?.sectionId !== this.dataset.sectionId || !data.variant) return;

        this.mainVariant = this.findVariant(this.bundleData.main, data.variant.id) || {
          id: data.variant.id,
          title: data.variant.title,
          price: data.variant.price,
          available: data.variant.available,
          image: data.variant.featured_image?.src || null,
        };

        this.updateMainProductRow();
        this.updateSummary();
        this.updateSubmitButton();
      }

      onModalClick(event) {
        const option = event.target.closest('[data-bundle-product-id]');
        if (!option) return;

        event.preventDefault();
        const product = this.bundleData.complementary.find(
          (item) => String(item.id) === String(option.dataset.bundleProductId)
        );
        if (!product) return;

        this.addonProduct = product;
        this.addonVariant = this.getInitialAddonVariant(product);
        this.dataset.selectedAddonProductId = product.id;

        this.renderAddonProduct();
        this.updateModalSelection();
        this.updateSummary();
        this.updateSubmitButton();
        this.modal?.hide();
      }

      onFormSubmit(event) {
        if (!this.isActive()) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.addBundleToCart();
      }

      async addBundleToCart() {
        if (this.submitButton?.getAttribute('aria-disabled') === 'true' || this.submitButton?.disabled) return;

        const mainVariantId = Number(this.form?.querySelector('[name="id"]')?.value || this.mainVariant?.id);
        const addonVariantId = Number(this.addonVariant?.id);
        const quantityInput = this.productInfo?.querySelector('.quantity__input');
        const quantity = Math.max(1, Number(quantityInput?.value) || 1);

        if (!mainVariantId || !addonVariantId) {
          this.showError(window.variantStrings?.unavailable || 'Tuote ei ole saatavilla');
          return;
        }

        this.setLoading(true);

        try {
          const response = await fetch(`${window.routes.cart_add_url}.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              items: [
                {
                  id: mainVariantId,
                  quantity,
                },
                {
                  id: addonVariantId,
                  quantity,
                },
              ],
            }),
          });
          const data = await response.json();

          if (!response.ok || data.status) {
            throw data;
          }

          window.location.href = this.dataset.checkoutUrl || '/checkout';
        } catch (error) {
          this.showError(error.description || error.message || window.cartStrings?.error || 'Tuotteita ei voitu lisätä koriin.');
          this.setLoading(false);
        }
      }

      updateView() {
        const active = this.isActive();

        this.classList.toggle('is-active', active);
        this.productInfo?.classList.toggle('bundle-upsell-active', active);
        this.updateHeaderCopy(active);
        this.updateSummary();
        this.updateSubmitButton();
      }

      updateHeaderCopy(active) {
        if (this.heading) {
          this.heading.textContent = active ? this.heading.dataset.activeText : this.heading.dataset.inactiveText;
        }

        if (this.description) {
          this.description.textContent = active
            ? this.description.dataset.activeText
            : this.description.dataset.inactiveText;
        }
      }

      updateMainProductRow() {
        const price = this.querySelector('[data-main-price]');
        if (price && this.mainVariant) price.textContent = this.formatMoney(this.mainVariant.price);

        const image = this.mainVariant?.image || this.bundleData.main.image;
        this.updateMedia(this.querySelector('[data-main-product-row] .bundle-upsell__media'), image, this.bundleData.main.title, 'mainImage');
      }

      renderAddonProduct() {
        const title = this.querySelector('[data-addon-title]');
        if (title) title.textContent = this.addonProduct.title;

        const image = this.addonVariant?.image || this.addonProduct.image;
        this.updateMedia(this.querySelector('[data-addon-row] .bundle-upsell__media'), image, this.addonProduct.title, 'addonImage');
        this.renderAddonVariantPicker();
        this.updateAddonPrice();
      }

      renderAddonVariantPicker() {
        if (!this.addonVariantContainer) return;

        if (this.addonProduct.hasOnlyDefaultVariant) {
          this.addonVariantContainer.innerHTML = '';
          return;
        }

        const label = this.getAddonVariantLabel(this.addonProduct);
        const selectId = `BundleAddonVariant-${this.dataset.sectionId}`;
        const options = this.addonProduct.variants
          .map((variant) => {
            const selected = String(variant.id) === String(this.addonVariant?.id) ? ' selected="selected"' : '';
            const disabled = variant.available ? '' : ' disabled';
            const soldOut = variant.available ? '' : ` - ${window.variantStrings?.soldOut || 'Loppuunmyyty'}`;

            return `<option value="${variant.id}" data-price="${variant.price}"${selected}${disabled}>${this.escapeHtml(
              variant.title
            )}${soldOut}</option>`;
          })
          .join('');

        this.addonVariantContainer.innerHTML = `
          <div class="product-form__input product-form__input--dropdown bundle-upsell__variant-field">
            <label class="form__label" for="${selectId}">${this.escapeHtml(label)}</label>
            <div class="select">
              <select id="${selectId}" class="select__select" data-addon-variant-select>${options}</select>
              ${this.caretMarkup}
            </div>
          </div>
        `;
      }

      updateAddonPrice() {
        const price = this.querySelector('[data-addon-price]');
        if (price && this.addonVariant) price.textContent = this.formatMoney(this.addonVariant.price);
      }

      updateSummary() {
        const active = this.isActive();
        const mainPrice = Number(this.mainVariant?.price || 0);
        const addonPrice = Number(this.addonVariant?.price || 0);
        const original = active ? mainPrice + addonPrice : mainPrice;
        const discounted = active ? Math.round(original * 0.8) : original;
        const saving = original - discounted;

        const label = this.querySelector('[data-summary-label]');
        const compare = this.querySelector('[data-bundle-compare]');
        const total = this.querySelector('[data-bundle-total]');
        const savingText = this.querySelector('[data-bundle-saving]');

        if (label) label.textContent = active ? 'Bundle-hinta' : 'Treeniohjelman hinta';
        if (compare) compare.textContent = this.formatMoney(original);
        if (total) total.textContent = this.formatMoney(discounted);
        if (savingText) savingText.textContent = `Säästät ${this.formatMoney(saving)}`;
      }

      updateSubmitButton() {
        if (!this.submitButton || !this.submitButtonText) return;

        const active = this.isActive();
        const mainAvailable = this.mainVariant?.available !== false;
        const addonAvailable = this.addonVariant?.available !== false;
        const disabledForBundle = active && (!mainAvailable || !addonAvailable);

        if (disabledForBundle) {
          this.submitButton.disabled = true;
          this.submitButton.dataset.bundleDisabled = 'true';
          this.submitButtonText.textContent = window.variantStrings?.soldOut || 'Loppuunmyyty';
          return;
        }

        if (this.submitButton.dataset.bundleDisabled === 'true') {
          this.submitButton.disabled = false;
          delete this.submitButton.dataset.bundleDisabled;
        }

        if (active && !this.submitButton.disabled) {
          this.submitButtonText.textContent = 'Siirry kassalle';
        } else if (!active && !this.submitButton.disabled) {
          this.submitButtonText.textContent = window.variantStrings?.addToCart || 'Lisää ostoskoriin';
        }
      }

      updateModalSelection() {
        if (!this.modal) return;

        this.modal.querySelectorAll('[data-bundle-product-id]').forEach((button) => {
          const selected = String(button.dataset.bundleProductId) === String(this.addonProduct.id);
          button.classList.toggle('is-selected', selected);

          const status = button.querySelector('.bundle-upsell-modal__status');
          if (status) status.textContent = selected ? 'Valittu' : 'Valitse';
        });
      }

      updateMedia(container, image, title, dataKey) {
        if (!container || !image) return;

        let img = container.querySelector('img');
        if (!img) {
          container.innerHTML = '';
          img = document.createElement('img');
          img.width = 90;
          img.height = 90;
          img.loading = 'lazy';
          if (dataKey) img.dataset[dataKey] = '';
          container.appendChild(img);
        }

        img.src = image;
        img.alt = title || '';
      }

      getCurrentMainVariant() {
        const variantId = this.form?.querySelector('[name="id"]')?.value || this.bundleData.main.selectedVariantId;
        return this.findVariant(this.bundleData.main, variantId) || this.bundleData.main.variants[0];
      }

      getInitialAddonProduct() {
        return (
          this.bundleData.complementary.find((product) => String(product.id) === String(this.dataset.selectedAddonProductId)) ||
          this.bundleData.complementary[0]
        );
      }

      getInitialAddonVariant(product) {
        const selected = this.findVariant(product, product?.selectedVariantId);
        if (selected?.available) return selected;
        return product?.variants?.find((variant) => variant.available) || product?.variants?.[0];
      }

      getAddonVariantLabel(product) {
        if (product.options?.length === 1 && product.options[0] !== 'Title') {
          return `Valitse ${product.options[0].toLocaleLowerCase('fi')}`;
        }

        return 'Valitse vaihtoehto';
      }

      findVariant(product, variantId) {
        return product?.variants?.find((variant) => String(variant.id) === String(variantId));
      }

      isActive() {
        return this.checkbox?.checked !== false;
      }

      setLoading(loading) {
        if (!this.submitButton) return;

        this.submitButton.classList.toggle('loading', loading);
        if (loading) {
          this.submitButton.setAttribute('aria-disabled', 'true');
        } else {
          this.submitButton.removeAttribute('aria-disabled');
        }
        this.productForm?.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);
      }

      showError(message) {
        if (typeof this.productForm?.handleErrorMessage === 'function') {
          this.productForm.handleErrorMessage(message);
        }
      }

      formatMoney(cents) {
        const amount = Number(cents || 0) / 100;
        const locale = this.dataset.locale || document.documentElement.lang || 'fi-FI';
        const currency = this.dataset.currency || 'EUR';

        try {
          return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency,
          }).format(amount);
        } catch (error) {
          return `${amount.toFixed(2)} ${currency}`;
        }
      }

      escapeHtml(value) {
        return String(value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#039;');
      }
    }
  );
}
